/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { TooltipDefinition, Link } from 'carbon-addons-iot-react';
import classnames from 'classnames';

import styles from './FormLabelTooltip.module.css';

const propTypes = {
  label: PropTypes.node.isRequired,
  tooltip: PropTypes.string,
  learnMore: PropTypes.string,
  required: PropTypes.bool,
  tooltipDirection: PropTypes.oneOf(['bottom', 'top']),
  tooltipAlign: PropTypes.oneOf(['start', 'center', 'end']),
  className: PropTypes.string,
};
const defaultProps = {
  tooltip: null,
  learnMore: null,
  className: null,
  required: false,
  tooltipDirection: 'bottom',
  tooltipAlign: 'start',
};

const messages = defineMessages({
  optional: '{label} (Optional)',
  learnMore: 'Learn more',
});

/**
 * Used to render a label and tooltip together
 */
const FormLabelTooltip = ({
  required,
  label,
  tooltip,
  tooltipDirection,
  tooltipAlign,
  intl: { formatMessage },
  learnMore,
  className,
}) => {
  const labelText = required ? label : formatMessage(messages.optional, { label });

  if (tooltip) {
    return (
      <TooltipDefinition
        className={classnames(styles.tooltip, { [className]: className })}
        tooltipText={
          <>
            {tooltip}
            {learnMore ? (
              <Link className={styles.learnMore} href={learnMore}>
                {formatMessage(messages.learnMore)}
              </Link>
            ) : null}
          </>
        }
        id={`tooltip-for-${labelText}`}
        direction={tooltipDirection}
        align={tooltipAlign}>
        {labelText}
      </TooltipDefinition>
    );
  }
  return <div className={styles.labelText}>{labelText}</div>;
};

FormLabelTooltip.propTypes = propTypes;
FormLabelTooltip.defaultProps = defaultProps;

export default injectIntl(FormLabelTooltip);
